/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Checkbox, CircularProgress, List, ListItem, Stack, Typography } from '@mui/material';

import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import {
  convertToReadableFormat,
  getAcronymName,
  getStoredCustomerDetails,
  isDateBefore,
  itemsPerPage,
} from '../../../../../Shared/Utilities/utils';
import { BooleanType, TeamAssignmentActionType } from '../../../../../API';
import { OrganizationService } from '../../../../../Shared/Services/OrganizationService';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import DeletePopup from '../../../../../Shared/Components/Common/DeletePopup/DeletePopup';
import { DEFAULT_DATE, UserTypes } from '../../../../../Shared/Constants/App';
import { IUser, IUserActionDropdownProps } from '../../AllSchedules/AllSchedules.Model';

import { Icons } from '../../../../../Shared/Constants/Icons';

import theme from '../../../../../Shared/Themes/theme';

import { ScheduleDaysText, CreateUserButtonContainer } from '../../AllSchedules/AllJobs.Style';
import {
  AssignDurationContainer,
  CheckBoxContainer,
  DatePickerContainer,
  HeadingText,
  LogoWrapper,
} from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { CustomerContainer, SearchFieldContainer, SearchInput } from '../../../../../Shared/Components/Layout/styles';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import Modal from '../../../../Jobs/Utilities/Shared/Components/Modal';
import DateSelection from '../../../../../Shared/Components/Common/DatePicker/DateSelection';
import { FieldNames } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { FormValues } from '../AllJobs.Model';

const AssignUser: React.FC<IUserActionDropdownProps> = ({ type }) => {
  const [usersData, setUsersData] = useState<IUser[]>([]);
  const [totalCount, setTotalCount] = useState<any>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [allUsersData, setAllUsersData] = useState<IUser[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [isAssignDurationOpen, setIsAssignDurationOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(true);

  const location = useLocation();
  const { t } = useTranslation(['messages', 'jobManagement', 'dashboard']);
  const navigate = useNavigate();

  const { control, watch, setValue, getValues, setError, clearErrors } = useForm<FormValues>({
    defaultValues: {
      startDate: new Date(),
      endDate: location?.state?.selectedRowsData?.[0]?.jobEndDate
        ? new Date(location?.state?.selectedRowsData?.[0]?.jobEndDate)
        : DEFAULT_DATE,
    },
  });

  const watchFields = watch();

  const { userId } = getStoredCustomerDetails() || {};

  const startDate = watch(FieldNames.startDate);
  const endDate = watch(FieldNames.endDate);

  const jobEndDate = location.state?.selectedRowsData?.[0]?.jobEndDate;

  const handleToggle = (userId: string | null) => {
    if (!userId) return;
    setSelectedUsers((prevSelected: string[]) =>
      prevSelected.includes(userId) ? prevSelected.filter((id) => id !== userId) : [...prevSelected, userId]
    );
  };

  const getPopupMessage = (
    type: string | null | undefined,
    selectedUsersCount: number,
    selectedSchedulesCount: number
  ) => {
    const userType =
      selectedUsersCount === 1
        ? type === UserTypes.Supervisors
          ? t('assignJob:supervisor')
          : t('jobManagement:associate')
        : type === UserTypes.Supervisors
          ? t('jobManagement:supervisors')
          : t('dashboard:associates');

    const scheduleText = selectedSchedulesCount === 1 ? t('jobManagement:schedule') : t('jobManagement:schedules');

    return `${t('jobManagement:confirmAssignment')} ${selectedUsersCount} ${userType} ${t('jobManagement:to')} ${selectedSchedulesCount} ${t('jobManagement:selected')} ${scheduleText}?`;
  };

  const noOfSelectedSchedules = location?.state?.selectedRowsData?.length;

  const fetchAllUsers = useCallback(
    async (pageNumber?: number, searchString?: string) => {
      try {
        setIsLoading(true);
        const customerDetails = getStoredCustomerDetails();
        const loggedInVendorId = customerDetails?.customerId;
        const selectedVendorId = location?.state?.selectedRowsData[0]?.vendorId;
        const vendorId = selectedVendorId ?? loggedInVendorId;
        const booleanType = type === UserTypes.Associates ? BooleanType.True : BooleanType.False;
        const selectedRows = location?.state?.selectedRowsData;
        const isSPSchedule =
          selectedRows.length > 0 &&
          selectedRows.every((row: { scheduleName: string }) => row.scheduleName?.startsWith('SP -'));

        const response = await OrganizationService.getAllUsers(
          vendorId,
          '',
          '',
          pageNumber,
          10,
          !isSPSchedule ? booleanType : '',
          true,
          '',
          '',
          searchString || ''
        );
        const usersData = response.data.map((userData: any) => ({
          userId: userData?.userId,
          username: userData?.username,
          organizationId: userData?.organizationId,
          roleId: userData?.roles?.[0]?.roleId,
          roleName: userData?.roles?.[0]?.roleName,
          teamId: userData?.teamId,
          intId: userData?.intId,
          email: userData?.email,
        }));

        setAllUsersData((prevUsersData) => {
          const updatedUsersData = [...prevUsersData, ...usersData];
          return updatedUsersData.filter(
            (user, index, self) => user?.userId && index === self.findIndex((u) => u?.userId === user?.userId)
          );
        });
        setUsersData(usersData);
        setTotalCount(response?.metadata?.totalCount);
      } catch (error) {
        console.error(t('dashboard:errorFetchingUsers'));
      } finally {
        setIsLoading(false);
      }
    },
    [type, getStoredCustomerDetails, setIsLoading, setUsersData, setTotalCount, t]
  );

  const handleChange = useCallback(
    (event: any, value: number): void => {
      setCurrentPage(value);
      void fetchAllUsers(value, searchString);
    },
    [fetchAllUsers, searchString]
  );

  const handleAssignmentError = () => {
    setSnackbarSeverity('error');
    setIsConfirmLoading(false);
    setIsPopupOpen(false);
    setIsAssignDurationOpen(false);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleConfirmation = async (isSupervisor: boolean) => {
    setIsConfirmLoading(true);
    const selectedRowsData = location.state?.selectedRowsData || [];
    const payload = (selectedRowsData ?? []).flatMap((row: any) =>
      selectedUsers.map((userId) => {
        const user = allUsersData.find((u) => u?.userId === userId);
        return {
          workOrderId: row?.workOrderId || '',
          workOrderScheduleMasterId: row?.woScheduleMasterId || '',
          userId: userId || '',
          organizationId: user?.organizationId || null,
          roleId: user?.roleId || null,
          isSupervisor: isSupervisor,
          startDate: startDate || null,
          endDate: endDate || null,
          jobNumber: row?.jobNumber || '',
          employeeId: user?.intId || null,
        };
      })
    );
    const actionType = TeamAssignmentActionType.Add;
    try {
      const result = await AllJobsApiService.manageScheduleAssignmentsEndpoint(payload, actionType, userId);
      if (
        (result?.errors && result?.errors?.length > 0) ||
        (result?.data?.manageScheduleAssignmentsFailure &&
          result?.data?.manageScheduleAssignmentsFailure?.length > 0 &&
          result?.data?.manageScheduleAssignmentsFailure?.some(
            (failure: { errorMessage: string }) => failure?.errorMessage !== 'ASSIGNMENT_ALREADY_EXISTS'
          )) ||
        result?.data?.manageScheduleAssignmentsSuccess === null
      ) {
        const errorMessages =
          result.errors?.map((error: { message: any }) => error.message).join(', ') || t('messages:errorOccurred');
        setSnackbarMessage(errorMessages);
        setSnackbarOpen(true);
        handleAssignmentError();
      } else if (
        result?.data?.manageScheduleAssignmentsFailure &&
        result?.data?.manageScheduleAssignmentsFailure?.length > 0 &&
        result?.data?.manageScheduleAssignmentsFailure?.every(
          (failure: { errorMessage: string }) => failure?.errorMessage === 'ASSIGNMENT_ALREADY_EXISTS'
        )
      ) {
        setSnackbarMessage(t('messages:assignmentAlreadyExists'));
        setSnackbarOpen(true);
        handleAssignmentError();
      } else {
        const redirectPath = '/all-jobs/manage-associates-and-supervisors';
        const successMessage =
          type === UserTypes.Supervisors
            ? t('messages:successfullyAssignedSupervisor')
            : t('messages:successfullyAssignedAssociate');
        setSnackbarMessage(successMessage);
        setSnackbarOpen(true);
        setSnackbarSeverity('success');
        setTimeout(() => {
          navigate(redirectPath);
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to manage schedule assignments:', error);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleConfirmSelection = () => {
    setIsAssignDurationOpen(true);
  };

  const handleConfirmDuration = () => {
    setIsPopupOpen(true);
  };

  const handlePopupConfirm = () => {
    const isSupervisor = type === UserTypes.Supervisors;
    handleConfirmation(isSupervisor);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value ?? '';
    setSearchString(value);
  };

  const renderAssignDurationHeaderContent = () => {
    return <HeadingText sx={{ fontSize: theme.typography.h2?.fontSize }}>{t('assignJob:assignDuration')}</HeadingText>;
  };

  const toggleCheckbox = () => {
    setIsChecked((prev) => {
      const newValue = !prev;
      if (newValue) {
        setValue(FieldNames.startDate, new Date());
        setValue(FieldNames.endDate, jobEndDate ? new Date(jobEndDate as string) : DEFAULT_DATE);
      } else {
        setValue(FieldNames.startDate, null);
        setValue(FieldNames.endDate, null);
      }
      return newValue;
    });
  };

  const isDateRangeInvalid = (
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined
  ) => {
    if (!startDate || !endDate) {
      return false;
    }
    const start = typeof startDate === 'string' ? startDate : moment(startDate).format(FieldNames.dateFormat);
    const end = typeof endDate === 'string' ? endDate : moment(endDate).format(FieldNames.dateFormat);

    return !isDateBefore(start, end);
  };

  const handleErrorForEndDate = (isError: boolean, message = '') => {
    if (isError) {
      setError(FieldNames.endDate, { type: 'validate', message });
    } else {
      clearErrors(FieldNames.endDate);
    }
  };

  const disableUpdateButton = () => {
    const { startDate, endDate } = getValues();

    if (isDateRangeInvalid(startDate, endDate)) {
      handleErrorForEndDate(true, t('oneTimeJob:endDateShouldBeGreaterThanStartDate'));
      return true;
    }

    handleErrorForEndDate(false);

    return !(startDate && endDate);
  };

  const renderAssignDurationBodyContent = () => {
    return (
      <>
        <AssignDurationContainer>
          <CheckBoxContainer>
            <Box onClick={toggleCheckbox}>
              {isChecked ? (
                <img
                  src={Icons.CheckedIcon}
                  alt={t('altTexts:checked')}
                  style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                />
              ) : (
                <img
                  src={Icons.Unchecked}
                  alt={t('altTexts:unchecked')}
                  style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                />
              )}
            </Box>
            <Typography sx={{ marginLeft: '0.625rem' }}>{t('jobManagement:defaultDuration')}</Typography>
          </CheckBoxContainer>
          <DatePickerContainer>
            <DateSelection
              requiredText={t('oneTimeJob:startDateFieldReq')}
              control={control}
              fieldName={FieldNames.startDate}
              label={t('calendar:startDate')}
              iconColor="black"
              isExcludePastDate={false}
              disabled={isChecked}
              showSpecificRangeDates={{
                from: new Date(),
                to: location.state?.selectedRowsData?.[0]?.jobEndDate,
              }}
            />
            <DateSelection
              requiredText={t('oneTimeJob:endDateFieldReq')}
              control={control}
              fieldName={FieldNames.endDate}
              label={t('calendar:endDate')}
              iconColor="black"
              isExcludePastDate={false}
              disabled={isChecked}
              showSpecificRangeDates={{
                from: new Date(),
                to: location.state?.selectedRowsData?.[0]?.jobEndDate,
              }}
            />
          </DatePickerContainer>
        </AssignDurationContainer>
        <CreateUserButtonContainer sx={{ paddingTop: '0rem', gap: '2rem' }}>
          <Button
            type={ButtonType.Primary}
            label={t('dashboard:cancel')}
            onClick={() => {
              setIsAssignDurationOpen(false);
            }}
          />
          <Button
            type={ButtonType.Primary}
            label={t('vendor:confirm')}
            onClick={handleConfirmDuration}
            disabled={disableUpdateButton() || selectedUsers?.length === 0}
          />
        </CreateUserButtonContainer>
      </>
    );
  };

  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  const debouncedFetch = useCallback(
    debounce((search: string, page: number) => {
      fetchAllUsers(page, search);
    }, 1000),
    []
  );

  useEffect(() => {
    if (isInitialLoad) {
      fetchAllUsers(1, '');
      setIsInitialLoad(false);
    } else {
      debouncedFetch(searchString, 1);
    }
  }, [searchString]);

  useEffect(() => {
    disableUpdateButton();
  }, [watchFields]);

  useEffect(() => {
    if (isChecked) {
      setValue(FieldNames.startDate, new Date());
      setValue(FieldNames.endDate, jobEndDate ? new Date(jobEndDate as string) : null);
    }
  }, [isChecked, jobEndDate, setValue]);

  return (
    <>
      <PageTitle
        title={
          type === UserTypes.Associates ? t('jobManagement:assignAssociates') : t('jobManagement:assignSupervisors')
        }
      />
      <CustomerContainer>
        <Box>
          <SearchFieldContainer sx={{ marginBottom: '1rem' }}>
            <SearchInput placeholder={t('oneTimeJob:search')} value={searchString} onChange={handleSearchChange} />
            <img src={Icons.SearchIcon} alt={t('altTexts:search')} />
          </SearchFieldContainer>
        </Box>
        <Box sx={{ background: 'white', borderRadius: '1rem' }}>
          <Box sx={{ maxHeight: '600px', minHeight: '520px', overflowY: 'auto', padding: '2rem 2rem 0 2rem' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
              </Box>
            ) : usersData?.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                }}>
                <ScheduleDaysText>{t('assignJob:noResultsFound')}</ScheduleDaysText>
              </Box>
            ) : (
              <List sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
                {(usersData ?? [])?.map(({ userId, username, roleName, intId, email }) => (
                  <ListItem
                    key={userId}
                    onClick={() => handleToggle(userId)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      marginBottom: '0.75rem',
                      '&:hover': {
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                      },
                    }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: intId ? '0.625rem' : '1rem' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <LogoWrapper>
                          <Box className={intId ? 'logo-styles' : ''}>{getAcronymName(username)?.toUpperCase()}</Box>
                        </LogoWrapper>
                        {intId && (
                          <ScheduleDaysText
                            sx={{
                              marginBottom: 0,
                              color: theme.palette.primary.dark,
                              fontSize: theme.typography.h6?.fontSize,
                            }}>
                            {intId}
                          </ScheduleDaysText>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: intId ? 0.5 : 0 }}>
                        <ScheduleDaysText sx={{ marginBottom: 0, marginTop: intId ? '0.25rem' : '' }}>
                          {username}
                        </ScheduleDaysText>
                        <ScheduleDaysText
                          sx={{
                            marginBottom: 0,
                            color: theme.palette.secondary.customColor9,
                            fontSize: theme.typography.h6?.fontSize,
                            gap: '1rem',
                          }}>
                          <Box>{convertToReadableFormat(roleName)}</Box>
                          <Box>{'-'}</Box>
                          <Box>{email}</Box>
                        </ScheduleDaysText>
                      </Box>
                    </Box>
                    <Checkbox
                      checked={selectedUsers.includes(userId)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggle(userId)}
                      icon={<img src={Icons.Unchecked} alt="unchecked" />}
                      checkedIcon={
                        <img
                          src={Icons.CheckedIcon}
                          alt="checked"
                          style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                        />
                      }
                      disabled={isLoading}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          {!isLoading && totalCount > 10 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem' }}>
              <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                <StyledPagination
                  count={Math.ceil(totalCount / itemsPerPage)}
                  page={currentPage}
                  onChange={(event, value) => {
                    handleChange(event, value);
                  }}
                  color="primary"
                />
              </Stack>
            </Box>
          )}
        </Box>
        {!isLoading && (
          <CreateUserButtonContainer>
            <Button
              type={ButtonType.Primary}
              label="Create New User"
              onClick={() => navigate('/all-schedules/create-user', { state: { ...location.state, type } })}
            />
            <Button
              type={ButtonType.Primary}
              label={t('vendor:confirm')}
              onClick={handleConfirmSelection}
              disabled={selectedUsers.length === 0}
            />
          </CreateUserButtonContainer>
        )}
      </CustomerContainer>
      <DeletePopup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        message={getPopupMessage(type, selectedUsers.length, noOfSelectedSchedules || 0)}
        primaryButtonLabel={t('homePage:yes')}
        name={''}
        isPrimaryButtonLoading={isConfirmLoading}
        primaryAction={handlePopupConfirm}
        secondaryButtonLabel={t('homePage:no')}
        secondaryAction={() => {
          setIsPopupOpen(false);
        }}
      />
      <Modal
        open={isAssignDurationOpen}
        onClose={() => {
          setIsAssignDurationOpen(false);
        }}
        renderHeader={renderAssignDurationHeaderContent()}
        renderBody={renderAssignDurationBodyContent()}
      />
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default AssignUser;
